import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Button, Row, Col, Typography, Select, DatePicker, Space } from 'antd';

import { Encrypt } from '../utils/encryption';
import { userRegistration } from '../config/api'; // adjust path if needed
import brain from '../assets/brain.gif';
import '../styles/Register.css';
import axios from 'axios';

const { Title } = Typography;
const { Option } = Select;

// Country code data with full names and flags
const countryCodes = [
  { label: '🇦🇹 Austria (+43)', value: '+43' },
  { label: '🇦🇺 Australia (+61)', value: '+61' },
  { label: '🇧🇾 Belarus (+375)', value: '+375' },
  { label: '🇧🇪 Belgium (+32)', value: '+32' },
  { label: '🇧🇷 Brazil (+55)', value: '+55' },
  { label: '🇧🇬 Bulgaria (+359)', value: '+359' },
  { label: '🇨🇦 Canada (+1)', value: '+1-CA' },
  { label: '🇨🇳 China (+86)', value: '+86' },
  { label: '🇭🇷 Croatia (+385)', value: '+385' },
  { label: '🇨🇿 Czech Republic (+420)', value: '+420' },
  { label: '🇩🇰 Denmark (+45)', value: '+45' },
  { label: '🇫🇮 Finland (+358)', value: '+358' },
  { label: '🇫🇷 France (+33)', value: '+33' },
  { label: '🇩🇪 Germany (+49)', value: '+49' },
  { label: '🇬🇷 Greece (+30)', value: '+30' },
  { label: '🇭🇺 Hungary (+36)', value: '+36' },
  { label: '🇮🇸 Iceland (+354)', value: '+354' },
  { label: '🇮🇳 India (+91)', value: '+91' },
  { label: '🇮🇩 Indonesia (+62)', value: '+62' },
  { label: '🇮🇪 Ireland (+353)', value: '+353' },
  { label: '🇮🇱 Israel (+972)', value: '+972' },
  { label: '🇮🇹 Italy (+39)', value: '+39' },
  { label: '🇯🇵 Japan (+81)', value: '+81' },
  { label: '🇱🇻 Latvia (+371)', value: '+371' },
  { label: '🇱🇹 Lithuania (+370)', value: '+370' },
  { label: '🇱🇺 Luxembourg (+352)', value: '+352' },
  { label: '🇲🇩 Moldova (+373)', value: '+373' },
  { label: '🇲🇽 Mexico (+52)', value: '+52' },
  { label: '🇳🇱 Netherlands (+31)', value: '+31' },
  { label: '🇳🇿 New Zealand (+64)', value: '+64' },
  { label: '🇳🇴 Norway (+47)', value: '+47' },
  { label: '🇵🇭 Philippines (+63)', value: '+63' },
  { label: '🇵🇱 Poland (+48)', value: '+48' },
  { label: '🇷🇴 Romania (+40)', value: '+40' },
  { label: '🇷🇺 Russia (+7)', value: '+7' },
  { label: '🇷🇸 Serbia (+381)', value: '+381' },
  { label: '🇸🇬 Singapore (+65)', value: '+65' },
  { label: '🇸🇰 Slovakia (+421)', value: '+421' },
  { label: '🇸🇮 Slovenia (+386)', value: '+386' },
  { label: '🇿🇦 South Africa (+27)', value: '+27' },
  { label: '🇰🇷 South Korea (+82)', value: '+82' },
  { label: '🇪🇸 Spain (+34)', value: '+34' },
  { label: '🇸🇪 Sweden (+46)', value: '+46' },
  { label: '🇨🇭 Switzerland (+41)', value: '+41' },
  { label: '🇹🇷 Turkey (+90)', value: '+90' },
  { label: '🇺🇦 Ukraine (+380)', value: '+380' },
  { label: '🇬🇧 United Kingdom (+44)', value: '+44' },
  { label: '🇺🇸 United States (+1)', value: '+1' },
];

// 📱 Prefix (Country Code) Selector with search enabled
const prefixSelector = (
  <Form.Item name="prefix" noStyle initialValue="+91">
    <Select
      showSearch
      style={{ width: 195 }}
      placeholder="Select country"
      optionFilterProp="children"
      filterOption={(input, option) =>
        option.children.toLowerCase().includes(input.toLowerCase())
      }
    >
      {countryCodes.map((country) => (
        <Option key={country.value} value={country.value}>
          {country.label}
        </Option>
      ))}
    </Select>
  </Form.Item>
);

const Register = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const email = Form.useWatch('email', form);
  const [loading, setLoading] = useState(false);
  const [finalLoading, setFinalLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [isEmailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState(''); // 👈 Code input state
  const [responseMessage, setResponseMessage] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);

  const emailInputRef = useRef(null);  // Reference to the email input field

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleVerifyClick = async (action='') => {
    if (isEmailVerified && action !== 'Resend') {
      setEmailVerified(false); // Let user re-edit
      setShowCodeInput(false); // Optionally hide the code box
      return;
    }
    if (action === 'Resend') {
      setVerificationCode('');
      form.setFieldsValue({ verificationCode: '' });
      setEmailVerified(false); // Resend to same Email
    }

    setLoading(true);

    try {
      const response = await axios.post(userRegistration, Encrypt(email), {
        headers: { 'Content-Type': 'application/json' },
      });
      const token = response.data?.token;

      if (token) {
        setShowCodeInput(true); // 👈 Show input field for verification code
        setVerificationToken(token);
        setEmailVerified(true);
        form.setFieldsValue({ userName: email });
        setResendCountdown(46); // ⏱ Start 45 seconds
      }

    }catch (error) {
      const message = error?.response?.data?.message || 'Unexpected error occurred. Please try again later.';
      form.setFields([
        {
          name: 'email',
          errors: [message],
        },
      ]);
      console.error('Token generation failed:', error);

    }finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const onFinish = async (values) => {
    setFinalLoading(true);

    const formattedMobile = `${values.prefix}-${values.mobile}`; // Combine country code with mobile number
    const updatedValues = { 
      ...values, 
      action: 'register',
      token: verificationToken,
      mobile: formattedMobile,
      dateOfBirth: values.dateOfBirth
        ? values.dateOfBirth.format('YYYY-MMM-DD')
        : "", // Optional field
    };

    try {
      const response = await axios.post(userRegistration, Encrypt(updatedValues),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setResponseMessage(`${response.data?.message || '🤔Something is not right'}`);
      // ✅ Redirect if password reset is successful
      if (response.status === 200 && response.data?.message.toLowerCase().includes('success')) {
        setTimeout(() => {
          navigate('/login'); // 🔁 redirect to login page
        }, 1600); // Optionally delay so user sees success message
      }
      
    }catch (error) {
      console.error('Registration error:', error);
      setResponseMessage(`🤔${error.response?.data?.message || '🤔Something is not right'}`);

    }finally {
      setFinalLoading(false);
    }
  };

  // Checks if the register button should be enabled
  const isFormValid = () => {
    const values = form.getFieldsValue(['email', 'userName']);
    return values.email && values.userName;
  };

  return (
    <div className="register-container">
      <div className="Reg-logo-container">
        <img src={brain} alt="Rotating Brain" className="Reg-brain-logo" />
      </div>
      <Title level={2}>MindBank Account Opening Form</Title>

        <Form
          form={form}
          name="register"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          className="register-form"
        >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <div className="email-input-wrapper" style={{ position: 'relative' }}>
            <Input
                type="email"
                value={email}
                onChange={(e) => form.setFieldsValue({ email: e.target.value })}
                ref={emailInputRef}
                disabled={isEmailVerified}
                className="email-input"
                style={{ paddingRight: 100 }} // Leave space for button
            />
              <Button
                type="primary"
                className="inside-email-button"
                onClick={()=>handleVerifyClick('Edit')}
                disabled={loading || !isValidEmail(email)}
                loading={loading}
              >
                {loading
                  ? 'Verifying...'
                  : isEmailVerified
                  ? 'Edit'
                  : 'Verify'
                }
              </Button>
          </div>

          {showCodeInput && (
            <div className="inline-verification-block">
              <Form.Item
                name="verificationCode"
                noStyle
                rules={[{ required: true, message: 'Verification code is required' }]}
              >
                <Input
                  placeholder="Verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  maxLength={6}
                />
              </Form.Item>
              <Button
                type="link"
                onClick={()=>handleVerifyClick('Resend')}
                disabled={resendCountdown > 0}
              >
                {resendCountdown > 0 ? `↻ Resend (⏱ ${resendCountdown}s)` : '↻ Resend'}
              </Button>
            </div>
          )}
            
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter your first name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter your last name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Gender field added */}
        <Form.Item
          label="Gender"
          name="gender"
          rules={[{ required: true, message: 'Please select your gender' }]}
        >
          <Select placeholder="Select Gender">
            <Option value="Female">Female</Option>
            <Option value="Male">Male</Option>
            <Option value="Other">Other</Option>
          </Select>
        </Form.Item>

        {/* 💡 Enhanced Mobile Field with Country Code */}
        <Form.Item label="Mobile" required={false}>
          <Space.Compact style={{ width: '100%' }}>
            {prefixSelector}
            <Form.Item
              name="mobile"
              noStyle
              rules={[{ required: false, message: 'Please enter your mobile number' }]}
            >
              <Input style={{ width: 'calc(100% - 195px)' }} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>

        {/* 👇 Add this here */}
        <Form.Item
          label="Date of Birth"
          name="dateOfBirth"
        >
          <DatePicker
            format="YYYY-MMM-DD"
            style={{ width: '100%' }}
            placeholder="Format: 1995-Jan-12 Or pick from calendar"
            allowClear
            disabledDate={(current) => current && current >= new Date().setHours(0, 0, 0, 0)}
          />
        </Form.Item>

        <Form.Item
          label="User Name (Auto-assigned)"
          name="userName"
          rules={[{ required: true, message: 'Please choose a User Name' }]}
        >
          <Input disabled />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Please enter a password' },
                    { min: 5, message: 'Must be 5 characters or more' },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={finalLoading} disabled={!isFormValid()}>
            {finalLoading ? 'Creating Account...' : 'Register'}
          </Button>
        </Form.Item>
      </Form>


      {responseMessage && (
        <Card title="Status" style={{ marginTop: 24 }}>
          <pre>{JSON.stringify(responseMessage, null, 2)}</pre>
        </Card>
      )}
    </div>
  );
};

export default Register;
